import React, { useEffect, useState } from "react";
import "./TradeHistory.css";

const TradeHistory = () => {
  const [tradeData, setTradeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showModal, setShowModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockHistoricalData, setStockHistoricalData] = useState([]);
  const [realizedProfit, setRealizedProfit] = useState(0);
  const baseUrl = process.env.REACT_APP_SERVER_URL;

  // Mock data for demonstration when API is not available
  const mockData = {
    symbol: 'SLB',
    companyName: 'SLB Limited',
    currentPrice: 1802.60,
    change: 29.60,
    changePercent: 1.67,
    prevClose: 1873.00,
    open: 1789.80,
    high: 1808.90,
    low: 1772.30,
    close: 1802.60,
    vwap: 1797.09,
    tradedVolume: 0.28,
    tradedValue: 5.05,
    totalMarketCap: 19958.39,
    freeFloatMarketCap: 8304.58,
    weekHigh52: 2960.00,
    weekLow52: 1356.05,
    upperBand: 2127.60,
    lowerBand: 1418.40,
    priceBand: 20,
    dailyVolatility: 2.30,
    annualisedVolatility: 43.94,
    deliveryPercent: 51.58,
    pe: 53.01,
    industry: 'Abrasives & Bearings',
    listingDate: '11-Sep-2006'
  };

  const displayData = mockData;

  useEffect(() => {
    const fetchWatchlist = async () => {
      try {
        const response = await fetch(`${baseUrl}/get_watchlists/executed`);
        const data = await response.json();
        setWatchlist(data);
        if (data.length > 0) {
          setActiveSubmenu(data[0].id);
        }
      } catch (error) {
        console.error("Error fetching watchlist:", error);
      }
    };

    fetchWatchlist();
  }, []);

  useEffect(() => {
    const fetchTradeData = async () => {
      if (!activeSubmenu) return;
      setLoading(true);
      try {
        const response = await fetch(
          `${baseUrl}/api/trades?watchlist_id=${activeSubmenu}`
        );
        const data = await response.json();
        setTradeData(data);
        setSelectedTrades([]); // Clear selections on submenu change
      } catch (error) {
        console.error("Error fetching trade data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchRealizedProfit = async () => {
      if (!activeSubmenu) return;
      try {
        const response = await fetch(
          `${baseUrl}/api/realized-profit?watchlist_id=${activeSubmenu}`
        );
        const data = await response.json();
        console.log("Realized profit API response:", data);
        console.log("Realized profit value:", data.realizedprofit);
        const profit = parseFloat(data.realizedprofit) || 0;
        console.log("Parsed profit value:", profit);
        setRealizedProfit(profit);
      } catch (error) {
        console.error("Error fetching realized profit:", error);
        setRealizedProfit(0);
      }
    };

    fetchTradeData();
    fetchRealizedProfit();

    const interval = setInterval(() => {
      fetchTradeData();
      fetchRealizedProfit();
    }, 300000);

    return () => clearInterval(interval);
  }, [activeSubmenu]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    if (!sortConfig.key) return tradeData;

    return [...tradeData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle numeric fields
      if (['Qty', 'buy_price', 'total_buy', 'ltp', 'current_value', 'pl', 'holding_days'].includes(sortConfig.key)) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      // Handle P/L calculation for sorting
      if (sortConfig.key === 'pl') {
        aValue = a.current_value - a.total_buy;
        bValue = b.current_value - b.total_buy;
      }

      if (sortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort sort-icon"></i>;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fas fa-sort-up sort-icon active"></i>
      : <i className="fas fa-sort-down sort-icon active"></i>;
  };

  const handleRowClick = async (trade) => {
    setSelectedStock(trade);
    setShowModal(true);

    // Fetch historical data for the selected stock
    try {
      const response = await fetch(
        `${baseUrl}/api/stock-history?symbol=${trade.symbol}&instrument_key=${trade.instrument_key}`
      );
      const data = await response.json();
      setStockHistoricalData(data);
    } catch (error) {
      console.error("Error fetching stock historical data:", error);
      setStockHistoricalData([]);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedStock(null);
    setStockHistoricalData([]);
  };

  const handleSelectTrade = (instrumentKey, event) => {
    event.stopPropagation(); // Prevent row click when checkbox is clicked
    setSelectedTrades((prev) =>
      prev.includes(instrumentKey)
        ? prev.filter((item) => item !== instrumentKey)
        : [...prev, instrumentKey]
    );
  };

  const handleSelectAll = () => {
    const allKeys = tradeData.map((trade) => trade.instrument_key);
    setSelectedTrades(
      selectedTrades.length === allKeys.length ? [] : allKeys
    );
  };

  const markSelectedAsIMS = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/mark-ims`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ trade_keys: selectedTrades }),
      });

      const result = await response.json();
      if (result.success) {
        alert("Selected stocks marked as IMS successfully.");
        setSelectedTrades([]);
        // Refresh trade data after update
        const updatedResponse = await fetch(
          `${baseUrl}/api/trades?watchlist_id=${activeSubmenu}`
        );
        const updatedData = await updatedResponse.json();
        setTradeData(updatedData);
      } else {
        alert(result.message || "Failed to mark as IMS.");
      }
    } catch (error) {
      console.error("Error marking stocks as IMS:", error);
      alert("Server error.");
    }
  };

  const totalPL = tradeData.reduce(
    (total, trade) => total + (parseFloat(trade.pl) || 0),
    0
  );
  const totalInvested = tradeData.reduce(
    (total, trade) => total + (parseFloat(trade.total_buy) || 0),
    0
  );
  const totalCurrentValue = tradeData.reduce(
    (total, trade) => total + (parseFloat(trade.current_value) || 0),
    0
  );
  const plPercentage = totalInvested
    ? ((totalPL / totalInvested) * 100).toFixed(2)
    : "0.00";

  const sortedTradeData = getSortedData();

  return (
    <div className="trade-history">
      <h2>Trade History</h2>

      <nav className="submenu">
        {watchlist.map((item) => (
          <button
            key={item.id}
            className={activeSubmenu === item.id ? "active" : ""}
            onClick={() => setActiveSubmenu(item.id)}
          >
            {item.name}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your trade data...</p>
        </div>
      ) : (
        <>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-icon profit-loss">
                <i
                  className={
                    totalPL >= 0
                      ? "fas fa-chart-line"
                      : "fas fa-chart-line-down"
                  }
                ></i>
              </div>
              <div className="card-content">
                <h3>Unrealized P/L</h3>
                <p className={totalPL >= 0 ? "text-success" : "text-danger"}>
                  ₹{totalPL.toFixed(2)} ({plPercentage}%)
                </p>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-icon invested">
                <i className="fas fa-money-bill-wave"></i>
              </div>
              <div className="card-content">
                <h3>Total Invested</h3>
                <p>₹{totalInvested.toFixed(2)}</p>
                <small>Current: ₹{totalCurrentValue.toFixed(2)}</small>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-icon realized-profit">
                <i className="fas fa-coins"></i>
              </div>
              <div className="card-content">
                <h3>Realized Profit</h3>
                <p className={realizedProfit >= 0 ? "text-success" : "text-danger"}>
                  ₹{realizedProfit.toFixed(2)}
                </p>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-icon current-value">
                <i className="fas fa-wallet"></i>
              </div>
              <div className="card-content">
                <h3>Portfolio Value</h3>
                <p>₹{totalCurrentValue.toFixed(2)}</p>
                <small>Net Worth</small>
              </div>
            </div>
          </div>

          {selectedTrades.length > 0 && (
            <div className="sell-actions">
              <button className="btn-sell" onClick={markSelectedAsIMS}>
                Mark {selectedTrades.length} Stock
                {selectedTrades.length > 1 ? "s" : ""} as IMS
              </button>
            </div>
          )}

          <div className="table-container">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selectedTrades.length === tradeData.length &&
                        tradeData.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>#</th>
                  <th onClick={() => handleSort('symbol')} className="sortable-header">
                    Symbol {getSortIcon('symbol')}
                  </th>
                  <th onClick={() => handleSort('Qty')} className="sortable-header">
                    Quantity {getSortIcon('Qty')}
                  </th>
                  <th onClick={() => handleSort('buy_price')} className="sortable-header">
                    Buy Price {getSortIcon('buy_price')}
                  </th>
                  <th onClick={() => handleSort('total_buy')} className="sortable-header">
                    Invested {getSortIcon('total_buy')}
                  </th>
                  <th onClick={() => handleSort('ltp')} className="sortable-header">
                    Current Price {getSortIcon('ltp')}
                  </th>
                  <th onClick={() => handleSort('current_value')} className="sortable-header">
                    Current Value {getSortIcon('current_value')}
                  </th>
                  <th onClick={() => handleSort('pl')} className="sortable-header">
                    P/L {getSortIcon('pl')}
                  </th>
                  <th onClick={() => handleSort('holding_days')} className="sortable-header">
                    Age(D) {getSortIcon('holding_days')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedTradeData.map((trade, index) => {
                  const pl = trade.current_value - trade.total_buy;
                  const plPercentage = trade.total_buy
                    ? ((pl / trade.total_buy) * 100).toFixed(2)
                    : 0;

                  return (
                    <tr
                      key={trade.instrument_key}
                      className={`clickable-row ${selectedTrades.includes(trade.instrument_key)
                          ? "selected-row"
                          : ""
                        }`}
                      onClick={() => handleRowClick(trade)}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedTrades.includes(trade.instrument_key)}
                          onChange={(e) => handleSelectTrade(trade.instrument_key, e)}
                        />
                      </td>
                      <td>{index + 1}</td>
                      <td className="symbol-cell">{trade.symbol}</td>
                      <td>{trade.Qty}</td>
                      <td>₹{parseFloat(trade.buy_price).toFixed(2)}</td>
                      <td>₹{parseFloat(trade.total_buy).toFixed(2)}</td>
                      <td>₹{parseFloat(trade.ltp).toFixed(2)}</td>
                      <td
                        className={
                          trade.current_value > trade.total_buy ? "profit" : "loss"
                        }
                      >
                        ₹{parseFloat(trade.current_value).toFixed(2)}
                      </td>
                      <td className={pl >= 0 ? "profit" : "loss"}>
                        ₹{pl.toFixed(2)} ({plPercentage}%)
                      </td>
                      <td>{trade.holding_days}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Stock Details Modal */}
      {showModal && selectedStock && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedStock.symbol} - Stock Details</h2>
              <button className="close-button" onClick={closeModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            {/* Stock Information */}
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 15px' }}>
              {/* Main Stock Header */}
              <div style={{ backgroundColor: 'white', borderRadius: '8px', marginBottom: '20px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ backgroundColor: '#6f42c1', color: 'white', padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '24px' }}>{displayData.symbol}</h4>
                      <small style={{ fontSize: '14px', opacity: 0.9 }}>{displayData.companyName}</small>
                    </div>
                    <div style={{ display: 'flex', gap: '40px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ textAlign: 'center' }}>
                        <h2 style={{ margin: 0, fontSize: '32px', display: 'flex', alignItems: 'center' }}>
                          {displayData.currentPrice?.toFixed(2) || '--'}
                          <span style={{ color: displayData.change >= 0 ? '#28a745' : '#dc3545', marginLeft: '10px' }}>
                            ▲
                          </span>
                        </h2>
                        <small style={{ fontSize: '14px' }}>
                          {displayData.change?.toFixed(2)} ({displayData.changePercent?.toFixed(2)}%)
                        </small>
                      </div>
                      {[
                        { label: 'PREV. CLOSE', value: displayData.prevClose?.toFixed(2) },
                        { label: 'OPEN', value: displayData.open?.toFixed(2) },
                        { label: 'HIGH', value: displayData.high?.toFixed(2) },
                        { label: 'LOW', value: displayData.low?.toFixed(2) },
                        { label: 'CLOSE*', value: displayData.close?.toFixed(2) },
                        { label: 'VWAP', value: displayData.vwap?.toFixed(2) }
                      ].map((item, index) => (
                        <div key={index} style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>{item.label}</div>
                          <div style={{ fontSize: '16px' }}>{item.value || '--'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="historical-data-section">
                <h3>Recent Performance</h3>
                {stockHistoricalData.length > 0 ? (
                  <div className="historical-table-container">
                    <table className="historical-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Open</th>
                          <th>High</th>
                          <th>OH%</th>
                          <th>Low</th>
                          <th>OL%</th>
                          <th>Close</th>
                          <th>Volume</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockHistoricalData.slice(0, 10).map((data, index) => (
                          <tr key={index}>
                            <td>{data.date}</td>
                            <td>{parseFloat(data.open).toFixed(2)}</td>
                            <td>{parseFloat(data.high).toFixed(2)}</td>
                            <td>{parseFloat((data.high - data.open) / data.open * 100).toFixed(2)}</td>
                            <td>{parseFloat(data.low).toFixed(2)}</td>
                            <td>{parseFloat((data.low - data.open) / data.open * 100).toFixed(2)}</td>
                            <td>{parseFloat(data.close).toFixed(2)}</td>
                            <td>{data.volume?.toLocaleString() || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">Historical data not available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeHistory;